import { type FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";

import { LabeledCheckbox } from "./LabeledCheckbox";
import { SubmitOrCancelButtons } from "./SubmitOrCancelButtons";

import { t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { Tooltip } from "mds/components/Tooltip";
import { storeApi, useAppSelector } from "store/index";
import { selectDesignatedSection } from "store/selectors";
import { getAvServiceFormItems } from "utils/av";

type FormValues = {
  auto_recordings_enabled: boolean;
  av_enabled: boolean;
  av_service: string;
  mute_students_on_main_room_entry: boolean;
};

interface SectionSettingsVirtualClassFormProps {
  className?: string;
  onClose: () => void;
}

export const SectionSettingsVirtualClassForm: FC<SectionSettingsVirtualClassFormProps> = ({
  className,
  onClose,
}) => {
  const currentSection = useAppSelector(selectDesignatedSection);

  const form = useForm<FormValues>({
    defaultValues: {
      auto_recordings_enabled: currentSection?.auto_recordings_enabled,
      av_enabled: currentSection?.av_enabled,
      av_service: currentSection?.av_service,
      mute_students_on_main_room_entry: currentSection?.mute_students_on_main_room_entry,
    },
  });

  if (!currentSection) {
    return null;
  }

  const avEnabled = form.watch("av_enabled");
  const avService = form.watch("av_service");

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    trackEvent("Section Settings - Save changes", BUTTON_EVENT_CATEGORY);
    return storeApi.sections.partial_update({ id: currentSection.id, ...formData }).then(() => {
      toastSuccessMessage(t("success.toasts.updated_section_settings"));
      onClose();
    });
  };

  return (
    <FormProvider {...form}>
      <form
        className={twMerge(
          "flex flex-col gap-2 text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Tooltip text={t("settings.section_settings_av_enabled.tooltip")}>
          <LabeledCheckbox
            eventName="Section Settings - Virtual Classes toggle"
            registration={form.register("av_enabled")}
          >
            {t("settings.section_settings_av_enabled.label")}
          </LabeledCheckbox>
        </Tooltip>

        <Tooltip text={t("settings.section_settings_auto_mute.tooltip")}>
          <LabeledCheckbox
            eventName="Section Settings - Mute students on main room entry toggle"
            registration={form.register("mute_students_on_main_room_entry", {
              disabled: !avEnabled,
            })}
          >
            {t("settings.section_settings_auto_mute.label")}
          </LabeledCheckbox>
        </Tooltip>

        <Tooltip text={t("settings.section_settings_auto_recordings.tooltip")}>
          <LabeledCheckbox
            eventName="Section Settings - Auto record class toggle"
            registration={form.register("auto_recordings_enabled", {
              disabled: !avEnabled,
            })}
          >
            {t("settings.section_settings_auto_recordings.label")}
          </LabeledCheckbox>
        </Tooltip>

        <label className="flex items-center justify-between">
          <Tooltip text={t("settings.section_settings_av_service.tooltip")}>
            <span>{t("settings.section_settings_av_service.label")}</span>
          </Tooltip>
          <input type="hidden" {...form.register("av_service")} />
          <div className="inline-block">
            <DropdownMenu
              disabled={!avEnabled}
              eventName="Section Settings - AV service selection"
              eventPropertyKey="newAvService"
              items={getAvServiceFormItems()}
              selectedValue={avService}
              onSelectionChange={({ id }) => form.setValue("av_service", id)}
            />
          </div>
        </label>

        <SubmitOrCancelButtons className="pt-8" onCancel={onClose} />
      </form>
    </FormProvider>
  );
};
